import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { NetworkAppliance } from '../types/models/NetworkAppliance';
import type {
  ApiResponseNetworkApplianceList,
  NetworkApplianceResource,
} from '../types/api/ApiResponseNetworkAppliances';

interface UseNetworkAppliancesReturn {
  appliances: NetworkAppliance[];
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches the tenant's firewalls, gateways, or switches from
 * `GET /datacenters/networks/{resource}`. The three endpoints share one item shape
 * and differ only in their data key, so a single hook covers all three.
 *
 * These endpoints are not paginated (small per-tenant counts), so the full list is
 * returned in one call and filtered/paginated client-side by the caller.
 */
export const useNetworkAppliances = (resource: NetworkApplianceResource): UseNetworkAppliancesReturn => {
  const [appliances, setAppliances] = useState<NetworkAppliance[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAppliances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/networks/${resource}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Per the API contract these endpoints map controller exceptions to
      // JSend `error` at HTTP 200, so branch on the body status, not response.ok.
      const data = (await response.json()) as ApiResponseNetworkApplianceList;
      if (data.status === 'success' && data.data) {
        const list = data.data[resource] ?? [];
        setAppliances(list);
        setCount(data.data.count ?? list.length);
      } else {
        setAppliances([]);
        setCount(0);
        setError(data.message ?? `Failed to load ${resource}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `An error occurred while fetching ${resource}`);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, resource]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }
    fetchAppliances();
  }, [auth.isAuthenticated, fetchAppliances]);

  return { appliances, count, loading, error, refetch: fetchAppliances };
};
